import React, { useEffect, useMemo, useState } from "react"
import {
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    InputAdornment,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@material-ui/core"
import { makeStyles } from "@material-ui/core/styles"
import { Add, Delete, Edit } from "@material-ui/icons"
import ReaisMask from "../../../masks/reais"
import useLoadingStore from "../../../stores/LoadingStore"
import { useSnackbar } from "../../../utils/notificacoes"
import {
    obterAssinatura,
    removerRecurso,
    salvarRecurso,
    type AssinaturaResumo,
    type CatalogoRecurso,
    type TipoRecursoAssinatura,
} from "../../../services/assinatura"

/** Formata um número como moeda BRL (mesmo padrão Intl usado em outras telas). */
const formatarMoeda = (valor: number | null | undefined): string =>
    Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(valor || 0)

const useStyles = makeStyles(theme => ({
    container: {
        maxWidth: 900,
        margin: "0 auto",
        padding: theme.spacing(2),
    },
    titulo: {
        fontWeight: 700,
        letterSpacing: 1,
        color: "#2c3e50",
    },
    resumoCard: {
        padding: theme.spacing(2),
        marginTop: theme.spacing(2),
    },
    linhaResumo: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: theme.spacing(0.5, 0),
    },
    total: {
        fontWeight: 700,
        fontSize: "1.15rem",
        color: "#2e7d32",
    },
    secaoTitulo: {
        marginTop: theme.spacing(3),
        marginBottom: theme.spacing(1),
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        flexWrap: "wrap",
        gap: theme.spacing(1),
    },
    acoes: {
        display: "flex",
        gap: theme.spacing(1),
        justifyContent: "flex-end",
    },
}))

interface DialogState {
    modo: "adicionar" | "editar"
    tipo: TipoRecursoAssinatura | ""
    valor: number
    recursoId?: number
}

export function AssinaturaEstabelecimentoForm() {
    const classes = useStyles()
    const { enqueueSnackbar } = useSnackbar()
    const setLoading = useLoadingStore(s => s.setLoading)

    const [resumo, setResumo] = useState<AssinaturaResumo | null>(null)
    const [salvando, setSalvando] = useState(false)
    const [dialog, setDialog] = useState<DialogState | null>(null)

    const autorizacao = localStorage.getItem("chaveSistema") ?? ""

    const alertStart = (mensagem: string, variant: "success" | "error" | "info" | "warning") =>
        enqueueSnackbar(mensagem, { variant })

    const carregar = async () => {
        if (!autorizacao) return
        setLoading(true)
        try {
            const res = await obterAssinatura(autorizacao)
            if ("retornoErro" in res) {
                alertStart(res.mensagem || "Erro ao carregar a assinatura.", "error")
            } else {
                setResumo(res)
            }
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        carregar()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const podeEditarValor = resumo?.podeEditarValor ?? false

    /** Tipos ainda não ativos — os que podem ser adicionados. */
    const catalogoDisponivel = useMemo<CatalogoRecurso[]>(() => {
        if (!resumo) return []
        const ativos = new Set(resumo.recursos.filter(r => r.ativo).map(r => r.tipo))
        return resumo.catalogo.filter(c => !ativos.has(c.tipo))
    }, [resumo])

    /**
     * Linhas da tabela: uma por recurso do catálogo. Se houver um recurso ATIVO daquele tipo,
     * mostra o valor/status contratados; senão, o valor padrão do catálogo como "Inativo".
     */
    const linhas = useMemo(() => {
        if (!resumo) return []
        return resumo.catalogo.map(cat => {
            const ativo = resumo.recursos.find(r => r.tipo === cat.tipo && r.ativo)
            return {
                tipo: cat.tipo,
                descricao: cat.descricao,
                valor: ativo ? ativo.valor : cat.valorPadrao,
                ativo: Boolean(ativo),
                recursoId: ativo?.id,
            }
        })
    }, [resumo])

    const catalogoPorTipo = (tipo: TipoRecursoAssinatura | "") =>
        resumo?.catalogo.find(c => c.tipo === tipo)

    const abrirAdicionar = (tipo?: TipoRecursoAssinatura) => {
        const escolhido = tipo ?? catalogoDisponivel[0]?.tipo ?? ""
        const padrao = catalogoPorTipo(escolhido)?.valorPadrao ?? 0
        setDialog({ modo: "adicionar", tipo: escolhido, valor: padrao })
    }

    const abrirEditar = (tipo: TipoRecursoAssinatura, valor: number, recursoId: number) => {
        setDialog({ modo: "editar", tipo, valor, recursoId })
    }

    const trocarTipoDialog = (tipo: TipoRecursoAssinatura) => {
        const padrao = catalogoPorTipo(tipo)?.valorPadrao ?? 0
        setDialog(d => (d ? { ...d, tipo, valor: padrao } : d))
    }

    const aplicarResposta = (res: AssinaturaResumo) => {
        setResumo(res)
        setDialog(null)
    }

    const handleSalvar = async () => {
        if (!dialog || !autorizacao) return
        if (dialog.modo === "adicionar" && !dialog.tipo) {
            alertStart("Selecione o tipo do recurso.", "error")
            return
        }
        if (podeEditarValor && (!dialog.valor || dialog.valor < 0)) {
            alertStart("Informe um valor válido.", "error")
            return
        }
        setSalvando(true)
        setLoading(true)
        try {
            const corpo =
                dialog.modo === "editar"
                    ? { id: dialog.recursoId, valor: dialog.valor }
                    : {
                          tipo: dialog.tipo as TipoRecursoAssinatura,
                          // Só CLIENTEFIEL negocia valor; para o lojista o servidor usa o padrão.
                          valor: podeEditarValor ? dialog.valor : undefined,
                      }
            const res = await salvarRecurso(autorizacao, corpo)
            if ("retornoErro" in res) {
                alertStart(res.mensagem || "Erro ao salvar o recurso.", "error")
                return
            }
            aplicarResposta(res)
            alertStart(
                dialog.modo === "editar" ? "Valor atualizado." : "Recurso adicionado à assinatura.",
                "success"
            )
        } finally {
            setSalvando(false)
            setLoading(false)
        }
    }

    const handleRemover = async (recursoId: number, descricao: string) => {
        if (!autorizacao) return
        // eslint-disable-next-line no-alert
        if (!window.confirm(`Remover o recurso "${descricao}" da assinatura?`)) return
        setLoading(true)
        try {
            const res = await removerRecurso(autorizacao, recursoId)
            if ("retornoErro" in res) {
                alertStart(res.mensagem || "Erro ao remover o recurso.", "error")
                return
            }
            setResumo(res)
            alertStart("Recurso removido.", "success")
        } finally {
            setLoading(false)
        }
    }

    if (!resumo) {
        return (
            <Box className={classes.container}>
                <Typography variant="h5" className={classes.titulo}>
                    ASSINATURA
                </Typography>
            </Box>
        )
    }

    const recursosAtivos = resumo.recursos.filter(r => r.ativo)
    const dialogPadrao = catalogoPorTipo(dialog?.tipo ?? "")?.valorPadrao ?? 0

    return (
        <Box className={classes.container}>
            <Typography variant="h5" className={classes.titulo}>
                ASSINATURA
            </Typography>

            {/* Cabeçalho: base + adicionais + total */}
            <Paper className={classes.resumoCard} elevation={1}>
                <div className={classes.linhaResumo}>
                    <Typography variant="body1">Estabelecimento</Typography>
                    <Typography variant="body1" style={{ fontWeight: 600 }}>
                        {resumo.estabelecimento?.nome ?? "—"}
                    </Typography>
                </div>
                <div className={classes.linhaResumo}>
                    <Typography variant="body1">Mensalidade base</Typography>
                    <Typography variant="body1">{formatarMoeda(resumo.valorBase)}</Typography>
                </div>
                <div className={classes.linhaResumo}>
                    <Typography variant="body1">Adicionais</Typography>
                    <Typography variant="body1">{formatarMoeda(resumo.valorRecursos)}</Typography>
                </div>
                <Divider style={{ margin: "8px 0" }} />
                <div className={classes.linhaResumo}>
                    <Typography variant="body1" style={{ fontWeight: 700 }}>
                        Mensalidade total
                    </Typography>
                    <Typography className={classes.total}>
                        {formatarMoeda(resumo.valorMensalTotal)}
                    </Typography>
                </div>
            </Paper>

            {!resumo.possuiAssinatura && (
                <Box mt={2}>
                    <Chip
                        color="secondary"
                        label="Estabelecimento sem assinatura ativa — não é possível adicionar recursos."
                    />
                </Box>
            )}

            {/* Recursos adicionais */}
            <div className={classes.secaoTitulo}>
                <Typography variant="h6" style={{ fontWeight: 600 }}>
                    Recursos adicionais
                </Typography>
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<Add />}
                    disabled={!resumo.possuiAssinatura || catalogoDisponivel.length === 0}
                    onClick={() => abrirAdicionar()}
                >
                    Adicionar recurso
                </Button>
            </div>

            <Paper elevation={1}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Recurso</TableCell>
                            <TableCell align="right">Valor mensal</TableCell>
                            <TableCell align="center">Status</TableCell>
                            <TableCell align="right">Ações</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {linhas.map(linha => (
                            <TableRow key={linha.tipo}>
                                <TableCell>{linha.descricao}</TableCell>
                                <TableCell align="right">{formatarMoeda(linha.valor)}</TableCell>
                                <TableCell align="center">
                                    <Chip
                                        size="small"
                                        label={linha.ativo ? "Ativo" : "Inativo"}
                                        style={{
                                            backgroundColor: linha.ativo ? "#2e7d32" : "#bdbdbd",
                                            color: "#fff",
                                        }}
                                    />
                                </TableCell>
                                <TableCell align="right">
                                    <div className={classes.acoes}>
                                        {linha.ativo ? (
                                            <>
                                                {podeEditarValor && (
                                                    <Button
                                                        size="small"
                                                        startIcon={<Edit />}
                                                        onClick={() =>
                                                            abrirEditar(
                                                                linha.tipo,
                                                                linha.valor,
                                                                linha.recursoId as number
                                                            )
                                                        }
                                                    >
                                                        Valor
                                                    </Button>
                                                )}
                                                {podeEditarValor && (
                                                    <Button
                                                        size="small"
                                                        color="secondary"
                                                        startIcon={<Delete />}
                                                        onClick={() =>
                                                            handleRemover(
                                                                linha.recursoId as number,
                                                                linha.descricao
                                                            )
                                                        }
                                                    >
                                                        Remover
                                                    </Button>
                                                )}
                                            </>
                                        ) : (
                                            <Button
                                                size="small"
                                                color="primary"
                                                startIcon={<Add />}
                                                disabled={!resumo.possuiAssinatura}
                                                onClick={() => abrirAdicionar(linha.tipo)}
                                            >
                                                Adicionar
                                            </Button>
                                        )}
                                    </div>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </Paper>

            {/* Resumo financeiro */}
            <Typography variant="h6" style={{ fontWeight: 600, marginTop: 24, marginBottom: 8 }}>
                Resumo financeiro
            </Typography>
            <Paper className={classes.resumoCard} elevation={1}>
                <div className={classes.linhaResumo}>
                    <Typography variant="body1">Mensalidade base</Typography>
                    <Typography variant="body1">{formatarMoeda(resumo.valorBase)}</Typography>
                </div>
                {recursosAtivos.map(r => (
                    <div key={r.id} className={classes.linhaResumo}>
                        <Typography variant="body1">{r.descricao}</Typography>
                        <Typography variant="body1">{formatarMoeda(r.valor)}</Typography>
                    </div>
                ))}
                <Divider style={{ margin: "8px 0" }} />
                <div className={classes.linhaResumo}>
                    <Typography variant="body1" style={{ fontWeight: 700 }}>
                        TOTAL MENSAL
                    </Typography>
                    <Typography className={classes.total}>
                        {formatarMoeda(resumo.valorMensalTotal)}
                    </Typography>
                </div>
            </Paper>

            {/* Dialog adicionar / editar valor */}
            <Dialog open={dialog !== null} onClose={() => setDialog(null)} fullWidth maxWidth="xs">
                <DialogTitle>
                    {dialog?.modo === "editar" ? "Editar valor do recurso" : "Adicionar recurso"}
                </DialogTitle>
                <DialogContent>
                    <FormControl fullWidth variant="outlined" margin="normal">
                        <InputLabel id="tipo-recurso-label">Tipo do recurso</InputLabel>
                        <Select
                            labelId="tipo-recurso-label"
                            label="Tipo do recurso"
                            value={dialog?.tipo ?? ""}
                            disabled={dialog?.modo === "editar"}
                            onChange={e => trocarTipoDialog(e.target.value as TipoRecursoAssinatura)}
                        >
                            {dialog?.modo === "editar"
                                ? resumo.catalogo
                                      .filter(c => c.tipo === dialog?.tipo)
                                      .map(c => (
                                          <MenuItem key={c.tipo} value={c.tipo}>
                                              {c.descricao}
                                          </MenuItem>
                                      ))
                                : catalogoDisponivel.map(c => (
                                      <MenuItem key={c.tipo} value={c.tipo}>
                                          {c.descricao}
                                      </MenuItem>
                                  ))}
                        </Select>
                    </FormControl>

                    <TextField
                        fullWidth
                        variant="outlined"
                        margin="normal"
                        label="Valor mensal"
                        value={dialog?.valor ?? 0}
                        disabled={!podeEditarValor}
                        onChange={(e: { floatValue: number }) =>
                            setDialog(d => (d ? { ...d, valor: e.floatValue } : d))
                        }
                        InputProps={{
                            startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                            inputComponent: ReaisMask,
                        }}
                        inputProps={{ style: { textAlign: "right" } }}
                        helperText={
                            podeEditarValor
                                ? `Valor padrão: ${formatarMoeda(dialogPadrao)}`
                                : `Ao ativar, sua mensalidade passará a ${formatarMoeda(
                                      resumo.valorMensalTotal + (dialog?.valor ?? 0)
                                  )}.`
                        }
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialog(null)} disabled={salvando}>
                        Cancelar
                    </Button>
                    <Button
                        onClick={handleSalvar}
                        color="primary"
                        variant="contained"
                        disabled={salvando}
                    >
                        {dialog?.modo === "editar" ? "Salvar" : "Adicionar"}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    )
}
